import React, { useState, useRef, useEffect } from 'react'
import { Button, Input, InputRef } from 'antd'
import { Pencil } from 'lucide-react'

interface Props {
  id?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  align?: 'left' | 'right'
  smallIcon?: boolean
}

export const EditableTitle: React.FC<Props> = ({
  id,
  value,
  onChange,
  placeholder,
  className = '',
  align = 'left',
  smallIcon = false
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [tempValue, setTempValue] = useState(value)
  const inputRef = useRef<InputRef>(null)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing])

  useEffect(() => {
    setTempValue(value)
  }, [value])

  const handleSubmit = () => {
    setIsEditing(false)
    if (tempValue === value) {
      return
    }
    onChange(tempValue)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit()
    } else if (e.key === 'Escape') {
      setTempValue(value)
      setIsEditing(false)
    }
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        onBlur={handleSubmit}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        style={{ textAlign: align }}
      />
    )
  }

  return (
    <Button
      onFocus={() => setIsEditing(true)}
      id={id}
      type="text"
      className={`w-full h-auto py-1 px-2 hover:bg-gray-50 ${className}`}
      onClick={() => {
        setTempValue(value)
        setIsEditing(true)
      }}
    >
      <div
        className={`flex items-center gap-2 w-full ${align === 'right' ? 'justify-end' : 'justify-start'}`}
      >
        <span
          className={`flex-1 flex items-center gap-[8px] text-${align} text-wrap`}
          style={{
            justifyContent: align === 'right' ? 'flex-end' : 'flex-start'
          }}
        >
          {value || <span className="text-gray-400">{placeholder}</span>}{' '}
          <Pencil
            className={`${smallIcon ? 'h-3 w-3' : 'h-4 w-4'} text-gray-400 flex-shrink-0 hide-on-print`}
          />
        </span>
      </div>
    </Button>
  )
}
